import { useState, useEffect } from 'react';
import { sortByName } from '../lib/sortByName';
import { useTranslation } from 'react-i18next';
import { roiAPI, organizationsAPI } from '../lib/api';
import { useAuth } from '../lib/auth';
import { toast } from 'sonner';
import {
    DollarSign,
    TrendingUp,
    TrendingDown,
    ChevronLeft,
    ChevronRight,
    BarChart3,
    PieChart,
    Building2,
    ChevronDown,
    ChevronUp,
    Info
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '../components/ui/tooltip';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as RechartsTooltip,
    ResponsiveContainer,
    PieChart as RechartsPie,
    Pie,
    Cell,
    Legend,
    LineChart,
    Line
} from 'recharts';

const PLATFORM_COLORS = {
    google_reviews: '#FF00FF',
    facebook: '#1877F2',
    instagram: '#E4405F',
    trustpilot: '#00B67A',
    yelp: '#D32323',
    tripadvisor: '#34E0A1'
};

const PLATFORM_LABELS = {
    google_reviews: 'Google Reviews',
    facebook: 'Facebook',
    instagram: 'Instagram',
    trustpilot: 'Trustpilot',
    yelp: 'Yelp',
    tripadvisor: 'Tripadvisor'
};

const ROIDashboard = () => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(true);
    const [monthlyData, setMonthlyData] = useState(null);
    const [historicalData, setHistoricalData] = useState([]);
    const [organizations, setOrganizations] = useState([]);
    const [selectedOrg, setSelectedOrg] = useState(
        // Same contract as the Command Center drill-down: ?org=… wins on entry.
        new URLSearchParams(window.location.search).get('org') || '');
    const [showBreakdown, setShowBreakdown] = useState(false);
    
    // Current month selection
    const now = new Date();
    const [selectedYear, setSelectedYear] = useState(now.getFullYear());
    const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1);
    
    const { user, isSuperAdmin } = useAuth();

    useEffect(() => {
        loadInitialData();
    }, []);

    useEffect(() => {
        loadMonthlyData();
    }, [selectedYear, selectedMonth, selectedOrg]);

    const loadInitialData = async () => {
        try {
            // Load organizations for Super Admin
            if (isSuperAdmin()) {
                const orgsRes = await organizationsAPI.list();
                setOrganizations(sortByName(orgsRes.data));
            }
            
            // Load historical data
            const historicalRes = await roiAPI.getHistorical(12, selectedOrg || undefined);
            setHistoricalData(historicalRes.data.data);
            
        } catch (error) {
            toast.error('Failed to load ROI data');
        } finally {
            setLoading(false);
        }
    };

    const loadMonthlyData = async () => {
        try {
            const res = await roiAPI.getMonthly(selectedYear, selectedMonth, selectedOrg || undefined);
            setMonthlyData(res.data);
            
            // Reload historical when org changes
            const historicalRes = await roiAPI.getHistorical(12, selectedOrg || undefined);
            setHistoricalData(historicalRes.data.data);
        } catch (error) {
            toast.error('Failed to load monthly ROI data');
        }
    };

    const navigateMonth = (direction) => {
        let newMonth = selectedMonth + direction;
        let newYear = selectedYear;
        
        if (newMonth > 12) {
            newMonth = 1;
            newYear += 1;
        } else if (newMonth < 1) {
            newMonth = 12;
            newYear -= 1;
        }
        
        setSelectedMonth(newMonth);
        setSelectedYear(newYear);
    };

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2
        }).format(value);
    };

    const getMonthName = () => {
        const date = new Date(selectedYear, selectedMonth - 1);
        return date.toLocaleString('default', { month: 'long', year: 'numeric' });
    };

    // Prepare pie chart data
    const pieChartData = monthlyData?.platform_breakdown
        .filter(p => p.tap_count > 0)
        .map(p => ({
            name: PLATFORM_LABELS[p.platform] || p.platform,
            value: p.total_value,
            fill: PLATFORM_COLORS[p.platform] || '#888'
        })) || [];

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
            </div>
        );
    }

    return (
        <TooltipProvider>
            <div className="space-y-6" data-testid="roi-dashboard">
                {/* Header */}
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                    <div>
                        <h1 className="text-2xl font-bold text-foreground" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            ROI Dashboard
                        </h1>
                        <p className="text-muted-foreground mt-1">
                            Track the monetary value generated from your review campaigns
                        </p>
                    </div>
                    
                    {/* Organization Filter (Super Admin) */}
                    {isSuperAdmin() && organizations.length > 0 && (
                        <Select value={selectedOrg || '__all__'} onValueChange={(val) => setSelectedOrg(val === '__all__' ? '' : val)}>
                            <SelectTrigger className="w-[220px]" data-testid="org-filter">
                                <Building2 className="w-4 h-4 mr-2" />
                                <SelectValue placeholder={t('roiDashboard.allOrganizations')} />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="__all__">{t('roiDashboard.allOrganizations')}</SelectItem>
                                {organizations.map(org => (
                                    <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    )}
                </div>

                {/* Month Navigation */}
                <div className="flex items-center justify-center gap-4"
                    data-insight-id="reporting.workflow.timeframe-month-nav"
                    data-insight-label="Month navigation"
                >
                    <Button 
                        variant="outline" 
                        size="icon"
                        onClick={() => navigateMonth(-1)}
                        data-testid="prev-month"
                    >
                        <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <span className="text-lg font-semibold text-foreground min-w-[180px] text-center">
                        {getMonthName()}
                    </span>
                    <Button 
                        variant="outline" 
                        size="icon"
                        onClick={() => navigateMonth(1)}
                        disabled={selectedYear === now.getFullYear() && selectedMonth === now.getMonth() + 1}
                        data-testid="next-month"
                    >
                        <ChevronRight className="w-4 h-4" />
                    </Button>
                </div>

                {/* Main ROI Card */}
                <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white">
                    <CardContent className="pt-6">
                        <div className="text-center">
                            <div className="flex items-center justify-center gap-2 mb-2">
                                <DollarSign className="w-6 h-6" />
                                <span className="text-emerald-100 text-lg">{t('roiDashboard.valueGenerated')}</span>
                            </div>
                            <div className="text-5xl font-bold mb-4" data-testid="total-value"
                                data-insight-id="reporting.workflow.headline-value"
                                data-insight-label="Headline value for the month"
                            >
                                {formatCurrency(monthlyData?.total_value || 0)}
                            </div>
                            <div className="flex items-center justify-center gap-4 text-emerald-100">
                                <span>{monthlyData?.total_taps || 0} total taps</span>
                                <span>•</span>
                                <span>
                                    {monthlyData?.total_taps > 0 
                                        ? formatCurrency((monthlyData?.total_value || 0) / monthlyData.total_taps)
                                        : '$0.00'} avg per tap
                                </span>
                            </div>
                            
                            {/* Expand/Collapse Breakdown */}
                            <Button
                                variant="ghost"
                                className="mt-4 text-white hover:bg-emerald-400/20"
                                onClick={() => setShowBreakdown(!showBreakdown)}
                                data-testid="toggle-breakdown"
                                data-insight-id="reporting.workflow.breakdown-drill"
                                data-insight-label="Per-platform breakdown drill-in"
                            >
                                {showBreakdown ? (
                                    <>
                                        <ChevronUp className="w-4 h-4 mr-2" />
                                        Hide Breakdown
                                    </>
                                ) : (
                                    <>
                                        <ChevronDown className="w-4 h-4 mr-2" />
                                        View Detailed Breakdown
                                    </>
                                )}
                            </Button>
                        </div>
                    </CardContent>
                </Card>

                {/* Platform Breakdown (Expandable) */}
                {showBreakdown && (
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <PieChart className="w-5 h-5 text-emerald-600" />
                                Platform Performance This Month
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="grid lg:grid-cols-2 gap-6">
                                {/* Table */}
                                <div className="space-y-3">
                                    {monthlyData?.platform_breakdown.map(platform => (
                                        <div 
                                            key={platform.platform}
                                            className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-lg"
                                        >
                                            <div className="flex items-center gap-3">
                                                <div 
                                                    className="w-3 h-3 rounded-full"
                                                    style={{ backgroundColor: PLATFORM_COLORS[platform.platform] }}
                                                />
                                                <span className="font-medium">{platform.platform_label}</span>
                                            </div>
                                            <div className="text-right">
                                                <div className="font-semibold text-foreground">
                                                    {platform.tap_count} × {formatCurrency(platform.value_per_tap)} = {formatCurrency(platform.total_value)}
                                                </div>
                                                {platform.percentage > 0 && (
                                                    <div className="text-sm text-muted-foreground">
                                                        {platform.percentage}% of total
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                    
                                    {/* Total Row */}
                                    <div className="flex items-center justify-between p-3 bg-emerald-50 rounded-lg border-2 border-emerald-200">
                                        <span className="font-bold text-emerald-700">{t('roiDashboard.totalValue')}</span>
                                        <span className="font-bold text-emerald-700 text-lg">
                                            {formatCurrency(monthlyData?.total_value || 0)}
                                        </span>
                                    </div>
                                </div>

                                {/* Pie Chart */}
                                {pieChartData.length > 0 && (
                                    <div className="h-[300px]">
                                        <ResponsiveContainer width="100%" height="100%">
                                            <RechartsPie>
                                                <Pie
                                                    data={pieChartData}
                                                    cx="50%"
                                                    cy="50%"
                                                    innerRadius={60}
                                                    outerRadius={100}
                                                    paddingAngle={2}
                                                    dataKey="value"
                                                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                                                >
                                                    {pieChartData.map((entry, index) => (
                                                        <Cell key={`cell-${index}`} fill={entry.fill} />
                                                    ))}
                                                </Pie>
                                                <RechartsTooltip 
                                                    formatter={(value) => formatCurrency(value)}
                                                />
                                            </RechartsPie>
                                        </ResponsiveContainer>
                                    </div>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Historical Chart */}
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <BarChart3 className="w-5 h-5 text-emerald-600" />
                            Value Generated - Last 12 Months
                        </CardTitle>
                        <CardDescription>
                            Monthly ROI trend based on platform-specific tap values
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="h-[350px]">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={historicalData}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                    <XAxis 
                                        dataKey="month_name" 
                                        tick={{ fontSize: 12 }}
                                        tickLine={false}
                                    />
                                    <YAxis 
                                        tickFormatter={(value) => `$${value}`}
                                        tick={{ fontSize: 12 }}
                                        tickLine={false}
                                        axisLine={false}
                                    />
                                    <RechartsTooltip 
                                        formatter={(value, name) => [formatCurrency(value), 'Value Generated']}
                                        labelStyle={{ fontWeight: 'bold' }}
                                    />
                                    <Bar 
                                        dataKey="total_value" 
                                        fill="#10B981" 
                                        radius={[4, 4, 0, 0]}
                                        name="Value"
                                    />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </CardContent>
                </Card>

                {/* Taps Trend Line Chart */}
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <TrendingUp className="w-5 h-5 text-blue-600" />
                            Tap Volume Trend
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="h-[250px]">
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={historicalData}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                    <XAxis 
                                        dataKey="month_name" 
                                        tick={{ fontSize: 12 }}
                                        tickLine={false}
                                    />
                                    <YAxis 
                                        tick={{ fontSize: 12 }}
                                        tickLine={false}
                                        axisLine={false}
                                    />
                                    <RechartsTooltip />
                                    <Line 
                                        type="monotone" 
                                        dataKey="total_taps" 
                                        stroke="#3B82F6" 
                                        strokeWidth={2}
                                        dot={{ fill: '#3B82F6', strokeWidth: 2 }}
                                        name="Total Taps"
                                    />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </CardContent>
                </Card>

                {/* Platform Value Info */}
                <Card className="bg-slate-50 dark:bg-slate-800">
                    <CardContent className="pt-6">
                        <div className="flex items-start gap-3">
                            <Info className="w-5 h-5 text-muted-foreground mt-0.5" />
                            <div>
                                <h4 className="font-medium text-foreground mb-1">{t('roiDashboard.howCalculated')}</h4>
                                <p className="text-sm text-muted-foreground">
                                    Each tap is counted separately by platform. The value is calculated by multiplying 
                                    the tap count for each platform by its assigned value (e.g., Google Reviews = $7.50, 
                                    Yelp = $6.00). Values can be customized per organization by Super Admins in 
                                    Admin → Settings → Platform Values.
                                </p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </TooltipProvider>
    );
};

export default ROIDashboard;
